import React from "react";

interface ModuleMovedBannerProps {
  /** Optional title shown above the description */
  title?: string;
  /** Main description text */
  description: string;
  /** CTA link label */
  ctaLabel: string;
  /** CTA link target */
  ctaHref: string;
  /** Optional snapshot date label shown in italic */
  dataSnapshotLabel?: string;
}

/**
 * Module deprecation banner — subtle amber style.
 * Intentionally lower visual priority than ImpersonationBanner (security-critical).
 *
 * Used on legacy KFS/BUR pages to point users to the new Nabory module.
 * Not dismissable — intended as a permanent data-staleness indicator.
 */
const ModuleMovedBanner: React.FC<ModuleMovedBannerProps> = ({
  title,
  description,
  ctaLabel,
  ctaHref,
  dataSnapshotLabel,
}) => {
  // Defense in depth: only allow http(s):// or root-relative paths.
  // Fallback to "/" for any other scheme (e.g. javascript:, data:).
  const safeCta = /^(https?:\/\/|\/)/.test(ctaHref) ? ctaHref : "/";
  return (
    <div
      data-testid="module-moved-banner"
      // role="alert" + aria-live="polite": persistent archival banner.
      // Spec decision (2026-05-20-wylaczenie-starych-naborow): treat as priority context for screen readers
      // despite static content. See: ImpersonationBanner uses same pattern.
      role="alert"
      aria-live="polite"
      className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
    >
      {/* Warning icon */}
      <svg
        className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>

      <div className="flex-1 text-sm">
        {title && (
          <p className="mb-1 font-semibold">{title}</p>
        )}
        <p>
          {description}
          {dataSnapshotLabel && (
            <>
              {" "}
              <em>{dataSnapshotLabel}</em>
            </>
          )}
        </p>
        <p className="mt-2">
          <a
            data-testid="module-moved-cta"
            href={safeCta}
            className="font-medium underline hover:text-amber-700"
          >
            {ctaLabel}
          </a>
        </p>
      </div>
    </div>
  );
};

export default ModuleMovedBanner;
